import React, { useState, useEffect } from 'react';
import { adminAPI, productsAPI, depotsAPI, vehiclesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Plus, Users, GasPump, Warehouse, Truck, Trash, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const roleLabels = {
  admin: 'Admin',
  super_admin: 'Super Admin',
  telesales: 'Telesales',
  planner: 'Planner',
  dispatcher: 'Dispatcher',
  depot_ops: 'Depot Ops',
  driver: 'Driver',
  finance: 'Finance',
  customer: 'Customer'
};

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [depots, setDepots] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [isDepotDialogOpen, setIsDepotDialogOpen] = useState(false);
  const [isVehicleDialogOpen, setIsVehicleDialogOpen] = useState(false);

  const [newUser, setNewUser] = useState({
    email: '', password: '', first_name: '', last_name: '', role: 'telesales'
  });
  const [newProduct, setNewProduct] = useState({
    name: '', code: '', base_price: '', tax_rate: '20', unit: 'litres'
  });
  const [newDepot, setNewDepot] = useState({
    name: '', code: '', address_line1: '', city: '', postcode: ''
  });
  const [newVehicle, setNewVehicle] = useState({
    registration: '', name: '', total_capacity_litres: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, productsRes, depotsRes, vehiclesRes] = await Promise.all([
        adminAPI.listUsers(),
        productsAPI.list(),
        depotsAPI.list(),
        vehiclesAPI.list()
      ]);
      setUsers(usersRes.data);
      setProducts(productsRes.data);
      setDepots(depotsRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      console.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      await adminAPI.createUser(newUser);
      toast.success('User created');
      setIsUserDialogOpen(false);
      setNewUser({ email: '', password: '', first_name: '', last_name: '', role: 'telesales' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleCreateProduct = async () => {
    try {
      await productsAPI.create({
        ...newProduct,
        base_price: parseFloat(newProduct.base_price),
        tax_rate: parseFloat(newProduct.tax_rate)
      });
      toast.success('Product created');
      setIsProductDialogOpen(false);
      setNewProduct({ name: '', code: '', base_price: '', tax_rate: '20', unit: 'litres' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product');
    }
  };

  const handleCreateDepot = async () => {
    try {
      await depotsAPI.create(newDepot);
      toast.success('Depot created');
      setIsDepotDialogOpen(false);
      setNewDepot({ name: '', code: '', address_line1: '', city: '', postcode: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create depot');
    }
  };

  const handleCreateVehicle = async () => {
    try {
      await vehiclesAPI.create({
        ...newVehicle,
        total_capacity_litres: parseFloat(newVehicle.total_capacity_litres)
      });
      toast.success('Vehicle created');
      setIsVehicleDialogOpen(false);
      setNewVehicle({ registration: '', name: '', total_capacity_litres: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create vehicle');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await adminAPI.deleteUser(id);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await productsAPI.delete(id);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      <div>
        <h1 className="text-2xl font-heading font-bold">Administration</h1>
        <p className="text-muted-foreground">Manage users, products, depots, and vehicles</p>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users" className="gap-2">
            <Users className="w-4 h-4" />
            Users ({users.length})
          </TabsTrigger>
          <TabsTrigger value="products" className="gap-2">
            <GasPump className="w-4 h-4" />
            Products ({products.length})
          </TabsTrigger>
          <TabsTrigger value="depots" className="gap-2">
            <Warehouse className="w-4 h-4" />
            Depots ({depots.length})
          </TabsTrigger>
          <TabsTrigger value="vehicles" className="gap-2">
            <Truck className="w-4 h-4" />
            Vehicles ({vehicles.length})
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Users</CardTitle>
                <CardDescription>Manage system users and roles</CardDescription>
              </div>
              <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-400 text-white font-bold" data-testid="add-user-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>First Name *</Label>
                        <Input value={newUser.first_name} onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })} data-testid="user-first-name" />
                      </div>
                      <div className="space-y-2">
                        <Label>Last Name *</Label>
                        <Input value={newUser.last_name} onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })} data-testid="user-last-name" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="user-email" />
                    </div>
                    <div className="space-y-2">
                      <Label>Password *</Label>
                      <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="user-password" />
                    </div>
                    <div className="space-y-2">
                      <Label>Role *</Label>
                      <Select value={newUser.role} onValueChange={(val) => setNewUser({ ...newUser, role: val })}>
                        <SelectTrigger data-testid="user-role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(roleLabels).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleCreateUser} className="w-full bg-orange-500 hover:bg-orange-400 text-white font-bold" data-testid="save-user-btn">
                      Create User
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.first_name} {user.last_name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{roleLabels[user.role] || user.role}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={user.is_active ? 'status-success' : 'status-error'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(user.id)} className="text-red-600">
                          <Trash className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Products</CardTitle>
                <CardDescription>Manage fuel products and pricing</CardDescription>
              </div>
              <Dialog open={isProductDialogOpen} onOpenChange={setIsProductDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-400 text-white font-bold" data-testid="add-product-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Product</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Product Name *</Label>
                      <Input value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} placeholder="e.g., Red Diesel" data-testid="product-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Product Code *</Label>
                      <Input value={newProduct.code} onChange={(e) => setNewProduct({ ...newProduct, code: e.target.value })} placeholder="e.g., DERV" data-testid="product-code" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Base Price (£/L) *</Label>
                        <Input type="number" step="0.0001" value={newProduct.base_price} onChange={(e) => setNewProduct({ ...newProduct, base_price: e.target.value })} placeholder="1.2500" data-testid="product-price" />
                      </div>
                      <div className="space-y-2">
                        <Label>Tax Rate (%)</Label>
                        <Input type="number" value={newProduct.tax_rate} onChange={(e) => setNewProduct({ ...newProduct, tax_rate: e.target.value })} />
                      </div>
                    </div>
                    <Button onClick={handleCreateProduct} className="w-full bg-orange-500 hover:bg-orange-400 text-white font-bold" data-testid="save-product-btn">
                      Create Product
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Base Price</TableHead>
                    <TableHead>Tax</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell className="font-mono">{product.code}</TableCell>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell className="font-mono">£{product.base_price?.toFixed(4)}</TableCell>
                      <TableCell>{product.tax_rate}%</TableCell>
                      <TableCell>
                        <Badge className={product.is_active ? 'status-success' : 'status-error'}>
                          {product.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteProduct(product.id)} className="text-red-600">
                          <Trash className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Depots Tab */}
        <TabsContent value="depots">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Depots</CardTitle>
                <CardDescription>Manage depot locations</CardDescription>
              </div>
              <Dialog open={isDepotDialogOpen} onOpenChange={setIsDepotDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-400 text-white font-bold" data-testid="add-depot-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Depot
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Depot</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Depot Name *</Label>
                        <Input value={newDepot.name} onChange={(e) => setNewDepot({ ...newDepot, name: e.target.value })} data-testid="depot-name" />
                      </div>
                      <div className="space-y-2">
                        <Label>Code *</Label>
                        <Input value={newDepot.code} onChange={(e) => setNewDepot({ ...newDepot, code: e.target.value })} data-testid="depot-code" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Address *</Label>
                      <Input value={newDepot.address_line1} onChange={(e) => setNewDepot({ ...newDepot, address_line1: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>City *</Label>
                        <Input value={newDepot.city} onChange={(e) => setNewDepot({ ...newDepot, city: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Postcode *</Label>
                        <Input value={newDepot.postcode} onChange={(e) => setNewDepot({ ...newDepot, postcode: e.target.value })} />
                      </div>
                    </div>
                    <Button onClick={handleCreateDepot} className="w-full bg-orange-500 hover:bg-orange-400 text-white font-bold" data-testid="save-depot-btn">
                      Create Depot
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {depots.map((depot) => (
                    <TableRow key={depot.id}>
                      <TableCell className="font-mono">{depot.code}</TableCell>
                      <TableCell className="font-medium">{depot.name}</TableCell>
                      <TableCell>{depot.city}, {depot.postcode}</TableCell>
                      <TableCell>
                        <Badge className={depot.is_active ? 'status-success' : 'status-error'}>
                          {depot.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Vehicles</CardTitle>
                <CardDescription>Manage fleet vehicles</CardDescription>
              </div>
              <Dialog open={isVehicleDialogOpen} onOpenChange={setIsVehicleDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-400 text-white font-bold" data-testid="add-vehicle-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Vehicle
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Vehicle</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Registration *</Label>
                      <Input value={newVehicle.registration} onChange={(e) => setNewVehicle({ ...newVehicle, registration: e.target.value.toUpperCase() })} placeholder="AB12 CDE" data-testid="vehicle-reg" />
                    </div>
                    <div className="space-y-2">
                      <Label>Name/Description</Label>
                      <Input value={newVehicle.name} onChange={(e) => setNewVehicle({ ...newVehicle, name: e.target.value })} placeholder="Tanker 1" />
                    </div>
                    <div className="space-y-2">
                      <Label>Total Capacity (Litres) *</Label>
                      <Input type="number" value={newVehicle.total_capacity_litres} onChange={(e) => setNewVehicle({ ...newVehicle, total_capacity_litres: e.target.value })} placeholder="30000" data-testid="vehicle-capacity" />
                    </div>
                    <Button onClick={handleCreateVehicle} className="w-full bg-orange-500 hover:bg-orange-400 text-white font-bold" data-testid="save-vehicle-btn">
                      Create Vehicle
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Registration</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Capacity</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vehicles.map((vehicle) => (
                    <TableRow key={vehicle.id}>
                      <TableCell className="font-mono font-medium">{vehicle.registration}</TableCell>
                      <TableCell>{vehicle.name || '-'}</TableCell>
                      <TableCell className="font-mono">{vehicle.total_capacity_litres?.toLocaleString()} L</TableCell>
                      <TableCell>
                        <Badge className={vehicle.is_active ? 'status-success' : 'status-error'}>
                          {vehicle.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
